import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import PackageStepIndicator from '@/components/PackageStepIndicator';
import HowPackagesWork from '@/components/HowPackagesWork';
import { getPackage, getStepPath, getLevelStyles } from '@/data/packageData';
import { usePackage } from '@/context/PackageContext';

const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

const PackageIntro = () => {
  const { packageSlug } = useParams();
  const navigate = useNavigate();
  const pkg = getPackage(packageSlug);
  const { clientData } = usePackage();

  useEffect(() => {
    if (!pkg) navigate('/', { replace: true });
    window.scrollTo(0, 0);
  }, [pkg, navigate]);

  if (!pkg) return null;

  const levelStyles = getLevelStyles(pkg.colorKey);
  const firstName = clientData?.firstName || null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-8 sm:pb-12 bg-white">
        {/* Back link removed from top — now below heading */}

        {/* Logo */}
        <div className="mb-6 sm:mb-8 flex flex-col items-center gap-4">
          <img
            src={LOGO_URL}
            alt="TCA Auditors Logo"
            className="h-24 sm:h-32 w-auto object-contain select-none"
            draggable="false"
          />
          <img
            src={BANNER_URL}
            alt="Tax & Auditing Consultants - For Businesses & Individuals"
            className="h-[4.5rem] sm:h-24 w-auto object-contain"
          />
        </div>

        {/* Heading */}
        <div className="mb-4 pb-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            ACCOUNTING RETAINER AGREEMENT
          </h1>
        </div>

        {/* Top back button — matches Comparison Table style */}
        <div className="w-full max-w-3xl flex justify-start mb-6">
          <Button
            onClick={() => navigate('/')}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-welcome-top"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Welcome Screen
          </Button>
        </div>

        <Card className="w-full max-w-3xl border-0 shadow-card">
          <CardContent className="p-6 sm:p-10 space-y-8 text-sm sm:text-base leading-relaxed">

            {/* Package badge */}
            <div className="flex justify-center">
              <span
                className="text-sm font-bold px-4 py-1.5 rounded-full"
                style={levelStyles}
                data-testid="package-badge"
              >
                {pkg.fullName}
              </span>
            </div>

            {/* Salutation */}
            <p className="text-foreground">
              Dear <span className="font-medium">{firstName || 'Client'}</span>,
            </p>

            {/* Opening paragraph */}
            <p className="text-foreground">
              Please find enclosed our <strong>Accounting Retainer</strong> engagement letter for
              your review and signature, prepared following our assessment and discussions of your
              current business requirements.
            </p>

            {/* Divider */}
            <div className="border-t border-border" />

            {/* HOW OUR RETAINER PACKAGES WORK — shared component */}
            <HowPackagesWork />

            {/* Divider */}
            <div className="border-t border-border" />

            {/* RECOMMENDATION */}
            <div className="space-y-3">
              <p className="text-base font-extrabold text-tca-blue uppercase tracking-wide underline">
                RECOMMENDATION:
              </p>
              <p className="text-foreground">
                Following a thorough assessment of your business requirements, we categorised your
                business into the{' '}
                <span
                  className="inline-block text-xs font-bold px-3 py-0.5 rounded-full align-middle mx-1"
                  style={levelStyles}
                  data-testid="recommendation-badge"
                >
                  {pkg.fullName.toUpperCase()}
                </span>{' '}
                level and prepared a proposal in this document based on this package.
              </p>
            </div>

            {/* PROPOSAL ACCEPTANCE */}
            <div className="space-y-3">
              <p className="text-base font-extrabold text-tca-blue uppercase tracking-wide underline">
                PROPOSAL ACCEPTANCE:
              </p>
              <p className="text-foreground">
                This document is a live, interactive engagement letter. Please read each section
                carefully before proceeding to the next.
              </p>
              <p className="text-foreground">
                The review acceptance flow is very intuitive and will allow you to step through the
                services proposed, the scope, and an outline of when those services will be
                provided. We would also draw your attention to any included service terms as any
                fixed prices quoted will be conditional on these terms being met. The variable
                prices listed are merely quotes/estimates at this stage and could change based on
                negotiations and potential discounts.
              </p>
              <p className="text-foreground">
                You'll also be taken through the pricing schedule which outlines our prices for
                those services.
              </p>
              <p className="text-foreground">
                This engagement letter includes options which you can review by clicking{' '}
                <strong>'Select'</strong> – don't worry, your choice does not become final until
                you electronically sign the engagement letter at the final step.
              </p>
              <p className="text-foreground">
                If you have any questions please let us know.
              </p>
            </div>

            {/* Divider */}
            <div className="border-t border-border" />

            {/* Signature block */}
            <div className="space-y-3">
              <p className="text-foreground">Kind regards,</p>
              <div>
                <p className="font-semibold text-foreground">TC Auditors (Pty) Ltd</p>
                <p className="text-muted-foreground text-sm">Xero Gold Partner – South Africa</p>
              </div>

              <table className="text-sm text-foreground border-collapse mt-2" data-testid="signature-table">
                <tbody>
                  <tr>
                    <td className="font-bold pr-4 py-0.5 whitespace-nowrap align-top">Email:</td>
                    <td className="py-0.5">
                      <a
                        href="mailto:admin@tcauditors.co.za"
                        className="underline hover:opacity-75"
                      >
                        admin@tcauditors.co.za
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4 py-0.5 whitespace-nowrap align-top">Reception:</td>
                    <td className="py-0.5">079 461 7545</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4 py-0.5 whitespace-nowrap align-top">Back Office:</td>
                    <td className="py-0.5">082 699 3003</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4 py-0.5 whitespace-nowrap align-top">Help Desk:</td>
                    <td className="py-0.5">060 015 1110</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4 py-0.5 whitespace-nowrap align-top">Website:</td>
                    <td className="py-0.5">
                      <a
                        href="https://www.tcauditors.co.za"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:opacity-75"
                      >
                        www.tcauditors.co.za
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* CTA */}
            <div className="flex flex-col items-center pt-4">
              <Button
                onClick={() => navigate(getStepPath(packageSlug, 'details'))}
                data-testid="btn-start-application"
                className="btn-gold-glow bg-accent hover:bg-accent text-accent-foreground font-semibold py-3 px-10 h-12 text-base rounded-lg"
              >
                Begin Your Application &rarr;
              </Button>
            </div>

          </CardContent>
        </Card>

        {/* Bottom back button */}
        <div className="w-full max-w-3xl flex justify-start mt-4 mb-6">
          <Button
            onClick={() => navigate('/')}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-welcome-bottom"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Welcome Screen
          </Button>
        </div>
      </main>

      <div className="relative">
        <PackageStepIndicator pkg={pkg} currentStepKey="intro" />
        <img
          src={TCA_SHORT_LOGO}
          alt="TCA Logo"
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>
    </div>
  );
};

export default PackageIntro;
